import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  CheckCircle,
} from "@mui/icons-material";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import {
  formatNumber,
  toIntPercent,
  formatDuration,
} from "../../utils/constant";
import { Skeleton } from "@mui/material";

const MetricCardSkeleton = () => {
  return (
    <Card
      sx={{
        borderRadius: "14px",
        border: "1px solid #e5e7eb",
        boxShadow: "none",
        height: "150px",
      }}
    >
      <CardContent
        sx={{
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          justifyContent: "space-between",
        }}
      >
        {/* Top row */}
        <Stack direction="row" justifyContent="space-between">
          <Skeleton width={120} height={18} />
          <Skeleton variant="circular" width={28} height={28} />
        </Stack>

        {/* Value */}
        <Skeleton width={100} height={36} />

        {/* Trend */}
        <Skeleton width={140} height={14} />
      </CardContent>
    </Card>
  );
};

/* ===================== METRIC CARD ===================== */
const MetricCard = ({ title, value, changePercent, icon, color }) => {
  const showChange = typeof changePercent === "number" && changePercent !== 0;
  const isPositive = changePercent >= 0;

  return (
    <Card
      sx={{
        borderRadius: "14px",
        border: "1px solid #e5e7eb",
        boxShadow: "none",
        height: "150px",
      }}
    >
      <CardContent
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Top Row */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
          <Typography
            sx={{
              fontSize: "2rem",
              fontWeight: 700,
              color: "#424242",
            }}
          >
            {title}
          </Typography>

          <Box sx={{ color }}>
            {icon}
          </Box>
        </Stack>

        {/* Value */}
        <Typography
          sx={{
            fontSize: "28px",
            fontWeight: 700,
            color: "#020817",
          }}
        >
          {value}
        </Typography>

        {/* Trend (only if non-zero) */}
        {showChange && (
          <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
            {isPositive ? (
              <TrendingUp sx={{ fontSize: 16, color: "#22c55e" }} />
            ) : (
              <TrendingDown sx={{ fontSize: 16, color: "#ef4444" }} />
            )}
            <Typography
              sx={{
                fontSize: '1.4rem',
                fontWeight: 400,
                color: isPositive ? "#22c55e" : "#ef4444",
              }}
            >
              {Math.abs(changePercent)}%
            </Typography>
            <Typography
              sx={{
                fontSize: '1.4rem',
                fontWeight: 400,
                color: "#424242",
              }}
            >
              vs last period
            </Typography>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

/* ===================== DASHBOARD CARDS ===================== */
export const DashboardCards = ({ 
  loading = false,
  companyDashboardSummaryData 
}) => {
  const metrics = companyDashboardSummaryData?.metrics || {};

  return (
    <Box>
      <Grid container spacing={2}>
        {loading ? (
          [1, 2, 3, 4].map((_, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <MetricCardSkeleton />
            </Grid>
          ))
        ) : (
          <>
        <Grid item xs={12} sm={6} xl={3}>
          <MetricCard
            title="Total API Calls"
            value={formatNumber(metrics?.totalCalls?.current ?? 0)}
            changePercent={metrics?.totalCalls?.changePercent}
            icon={<TimelineOutlinedIcon sx={{ fontSize: "2.25rem" }} />}
            color="#6366f1"
          />
        </Grid>

        <Grid item xs={12} sm={6} xl={3}>
          <MetricCard
            title="Credits Used"
            value={formatNumber(metrics?.creditUsed?.current ?? 0)}
            changePercent={metrics?.creditUsed?.changePercent}
            icon={<CreditCardOutlinedIcon sx={{ fontSize: "2.25rem" }} />}
            color="#f97316"
          />
        </Grid>

        <Grid item xs={12} sm={6} xl={3}>
          <MetricCard
            title="Success Rate"
            value={toIntPercent(metrics?.successRate?.current ?? 0)}
            changePercent={metrics?.successRate?.changePercent}
            icon={<CheckCircle sx={{ fontSize: "2.25rem" }} />}
            color="#22c55e"
          />
        </Grid>

        <Grid item xs={12} sm={6} xl={3}>
          <MetricCard
            title="Avg Response"
            value={formatDuration(
              metrics?.avgResponseTimeMs?.current ?? 0
            )}
            changePercent={metrics?.avgResponseTimeMs?.changePercent}
            icon={<AccessTimeOutlinedIcon sx={{ fontSize: "2.25rem" }} />}
            color="#3b82f6"
          />
        </Grid>
        </>
        )}
      </Grid>
    </Box>
  );
};